import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTable, useSortBy, usePagination, useGlobalFilter } from 'react-table';
import './AdminDataTable.css';

const AdminDataTable = ({
    columns,
    data,
    title,
    icon,
    loading = false,
    pageSize = 10,
    searchable = true,
    searchPlaceholder = "Search...",
    noDataMessage = "No data found",
    noDataImage = "/images/No-data-cuate.png",
    serverSidePagination = null, // { currentPage, totalPages, total, perPage, onPageChange }
    onSearch = null, // For server-side search
    debounceDelay = 500, // Debounce delay in milliseconds
    filters = null, // Filter elements to render next to search
}) => {
    const [searchValue, setSearchValue] = useState('');
    const debounceTimerRef = useRef(null);

    const memoizedColumns = useMemo(() => columns, [columns]);
    const memoizedData = useMemo(() => data || [], [data]);

    // For server-side pagination, use a large page size so all returned data is shown
    // react-table handles client-side pagination, we handle server-side manually
    const effectivePageSize = serverSidePagination ? 1000 : pageSize;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        setGlobalFilter,
        gotoPage,
        pageCount: clientPageCount,
        state: { pageIndex },
    } = useTable(
        {
            columns: memoizedColumns,
            data: memoizedData,
            initialState: { pageSize: effectivePageSize },
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Handle search with debouncing
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchValue(value);

        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set new timer
        debounceTimerRef.current = setTimeout(() => {
            if (onSearch) {
                // Server-side search
                onSearch(value);
            } else {
                // Client-side search
                setGlobalFilter(value || undefined);
            }
        }, onSearch ? debounceDelay : 0); // Only debounce server-side search
    };

    // Pagination logic
    const isServerSide = serverSidePagination !== null;
    const currentPage = isServerSide ? (serverSidePagination.currentPage || 1) : pageIndex + 1;
    const totalPages = isServerSide ? (serverSidePagination.totalPages || 1) : clientPageCount;
    const total = isServerSide ? (serverSidePagination.total || 0) : memoizedData.length;
    const perPage = isServerSide ? (serverSidePagination.perPage || 10) : pageSize;

    const startItem = total > 0 ? ((currentPage - 1) * perPage) + 1 : 0;
    const endItem = total > 0 ? Math.min(currentPage * perPage, total) : 0;

    const handlePageChange = (newPage) => {
        if (isServerSide) {
            serverSidePagination.onPageChange(newPage);
        } else {
            gotoPage(newPage - 1);
        }
    };

    // Generate page numbers
    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        const safeTotalPages = Math.max(1, totalPages || 1);

        if (safeTotalPages <= maxVisible) {
            for (let i = 1; i <= safeTotalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');

            const start = Math.max(2, currentPage - 1);
            const end = Math.min(safeTotalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                if (!pages.includes(i)) pages.push(i);
            }

            if (currentPage < safeTotalPages - 2) pages.push('...');
            if (!pages.includes(safeTotalPages)) pages.push(safeTotalPages);
        }
        return pages;
    };

    return (
        <div className="admin-dt-card">
            {/* Header */}
            <div className="admin-dt-header">
                <h2 className="admin-dt-title">
                    {icon && <i className={icon}></i>}
                    {title}
                </h2>
                <div className="admin-dt-header-actions">
                    {filters && (
                        <div className="admin-dt-filters">
                            {filters}
                        </div>
                    )}
                    {searchable && (
                        <div className="admin-dt-search">
                            <input
                                value={searchValue}
                                onChange={handleSearch}
                                placeholder={searchPlaceholder}
                                className="admin-dt-search-input"
                            />
                            <i className="fa-solid fa-magnifying-glass"></i>
                        </div>
                    )}
                </div>
            </div>

            {/* Table Body */}
            <div className="admin-dt-body">
                {loading ? (
                    <div className="admin-dt-loading">
                        <div className="admin-dt-loader"></div>
                    </div>
                ) : memoizedData.length > 0 ? (
                    <div className="admin-dt-table-wrapper">
                        <table {...getTableProps()} className="admin-dt-table">
                            <thead>
                                {headerGroups.map(headerGroup => (
                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map(column => (
                                            <th
                                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                                className={`${column.className || ''} ${column.canSort ? 'sortable' : ''}`}
                                            >
                                                <div className="admin-dt-th-content">
                                                    {column.render('Header')}
                                                    {column.canSort && (
                                                        <span className="admin-dt-sort-icon">
                                                            {column.isSorted ? (
                                                                column.isSortedDesc ? (
                                                                    <i className="fas fa-sort-down"></i>
                                                                ) : (
                                                                    <i className="fas fa-sort-up"></i>
                                                                )
                                                            ) : (
                                                                <i className="fas fa-sort"></i>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                                {page.map(row => {
                                    prepareRow(row);
                                    return (
                                        <tr {...row.getRowProps()}>
                                            {row.cells.map(cell => (
                                                <td
                                                    {...cell.getCellProps()}
                                                    className={cell.column.className || ''}
                                                >
                                                    {cell.render('Cell')}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="admin-dt-no-data">
                        <img src={noDataImage} alt="No Data" />
                        <p>{noDataMessage}</p>
                    </div>
                )}
            </div>

            {/* Footer - Pagination */}
            {!loading && memoizedData.length > 0 && totalPages > 1 && (
                <div className="admin-dt-footer">
                    <div className="admin-dt-info">
                        Showing <span>{startItem}</span> to <span>{endItem}</span> of <span>{total}</span> entries
                    </div>
                    <div className="admin-dt-pagination">
                        <button
                            className="admin-dt-page-btn"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            <i className="fas fa-chevron-left"></i>
                            <span>Previous</span>
                        </button>

                        <div className="admin-dt-page-numbers">
                            {getPageNumbers().map((pageNum, index) => (
                                pageNum === '...' ? (
                                    <span key={`ellipsis-${index}`} className="admin-dt-ellipsis">...</span>
                                ) : (
                                    <button
                                        key={pageNum}
                                        className={`admin-dt-page-btn admin-dt-page-num ${currentPage === pageNum ? 'active' : ''}`}
                                        onClick={() => handlePageChange(pageNum)}
                                    >
                                        {pageNum}
                                    </button>
                                )
                            ))}
                        </div>

                        <button
                            className="admin-dt-page-btn"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            <span>Next</span>
                            <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDataTable;
